// FlixPlayer.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { throttle } from 'lodash';
import { withTranslation } from 'react-i18next';
import { withScope, captureException } from '@sentry/browser';
import { createPortal } from 'react-dom';

import {
  aggressive,
  getTouchY,
  getShouldCancelScroll,
} from '../resource/scrollUtils.js';
import {
  getIsOnIOS,
  getIsIphoneXSeries,
  getIsOnMobile,
  getIsEdgeChromium,
  getIsOnMobileBySize,
} from '../resource/getUserAgent.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import trackMixpanel, { getViewId } from '../resource/mixpanel.js';
import { EventTypes, ButtonId } from '../resource/trackingConstants.js';
import { LOAD_STATE, PLAY_STATE } from '../resource/playerUtils.js';
import PlayerPortal from './PlayerPortal.jsx';
import PlayerControls from './PlayerControls.jsx';
import MessageVideoPlayer from '../container/MessageVideoPlayer.js';
import FlixPlayerActionButton from '../container/FlixPlayerActionButton.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import FadeIn from '../style/FadeIn.js';
import { flixPlayerZIndex } from '../style/variables.js';
import { mobileAspectRatio } from '../style/media.js';
import FlixPlayerGestureDetector, {
  MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS,
} from '../component/FlixPlayerGestureDetector.jsx';
import TopVideoPopupNotification from '../container/TopVideoPopupNotification.js';
import FlixPlayerHelper from '../component/FlixPlayerHelper.jsx';
import FlixPlayerZIndexChecker from '../component/FlixPlayerZIndexChecker.jsx';
import Loading, { LoadingType } from '../component/Loading.jsx';

import PlayIcon from '../../img/ic-play-white.svg';
import ReplayIcon from '../../img/icon-retry-outline-shadow.svg';

import ErrorIcon from '../../img/icon-info-outline-shadow.svg';
import FlixPlayerNoSeekDetector from '../resource/FlixPlayerNoSeekDetector.js';
import FlixPlayerDisturber from '../resource/FlixPlayerDisturber.js';
import { getMarkedAction as getMarkedDisturbingAction } from '../resource/disturbing.js';
import { DrmFeatureName } from '../resource/drmConstants.js';
import VideoWatermark from '../component/VideoWatermark.jsx';
import FlixUiHandler from '../component/FlixUiHandler.jsx';
import withPlayerTrackingEvent, {
  VIDEO_CATEGORY,
} from '../resource/withPlayerTrackingEvent.js';
import { Observable } from 'rxjs';
import { debounceTime, mapTo, switchMap } from 'rxjs/operators';
import createTrackingEvent from '../resource/createTrackingEvent.js';
import { PlayerType } from '../resource/engagementConstants.js';

const JUST_TAPPED_STATE_DURATION = 3000; // TODO: remote config
const SAFE_AREA_INSET_BOTTOM = getIsIphoneXSeries() ? 34 : 0;

/**
 * @typedef {import('video.js').VideoJsPlayer} VideoJsPlayer
 *
 * @typedef {Object} Props
 * @property {string} messageId
 * @property {string} assetId
 * @property {boolean} canViewMedia
 * @property {boolean} isMuted
 * @property {(arg: {isMuted: boolean, hasFlixPlayed: boolean}) => void} setPlayerData
 *
 * @typedef {Object} State
 * @property {number} bufferedTime
 * @property {number} currentTime
 * @property {number} duration
 * @property {string} loadState
 * @property {string} playState
 * @property {boolean} isFullscreen
 * @property {boolean} isMoving
 * @property {boolean} isSeeking
 * @property {boolean} isHoveringControls
 * @property {boolean} justTapped
 * @property {boolean} isMobileControlsToggled
 *
 * @extends {React.PureComponent<Props, State>}
 */
export class FlixPlayer extends React.PureComponent {
  state = {
    playerKey: 'default',
    bufferedTime: 0,
    currentTime: 0,
    duration: 100,
    loadState: null,
    playState: PLAY_STATE.Pause,
    error: null,
    isMoving: false,
    justTapped: false,
    isMobileControlsToggled: false,
    startWatermarkFlag: null,
    isSeeking: false,
    isHoveringControls: false,
    seekingCurrentTime: null,
  };

  isPausedBeforeSeeking = false;
  /** @type {VideoJsPlayer} */
  player = null;
  /** @type {number} */
  movingTimeout = null;
  /** @type {number} */
  justTappedDelayTimeout = null;
  /** @type {number} */
  justTappedEndTimeout = null;
  // HACK: scroll lock
  containerRef = React.createRef();
  playerUiContainerRef = React.createRef();
  shouldPreventQueue = [];
  touchStartY = 0;

  trackedPathname = React.createRef();

  hasTimecodeSeeked = false;

  isOnMobile = getIsOnMobile();

  shouldUpdateCurrentTimeOnSeeking = !getIsEdgeChromium();

  handleDocumentFullscreenChangeInterval = null;

  option = {};

  isBeginningSeeking = true;

  /** @type {FlixPlayerNoSeekDetector?} */
  noSeekDetector = null;
  /** @type {FlixPlayerDisturber?} */
  disturber = null;

  mountDisturber = (shouldDisturbImmediately = false) => {
    if (this.player && !this.disturber) {
      this.disturber = FlixPlayerDisturber.mount(
        this.player,
        this.handleDisturbed
      );
      if (shouldDisturbImmediately) {
        this.disturber.disturb();
      }
    }
  };

  /** @type {import('../resource/FlixPlayerNoSeekDetector').OnDetectedCallback} */
  handleNoSeekDetected = ({ noSeekDuration, messageDuration }) => {
    const { messageId, senderName } = this.props;
    trackMixpanel({
      type: EventTypes.USER_FLIX_NO_SEEK,
      payload: {
        messageId,
        userUsername: senderName,
        noSeekDuration,
        messageDuration,
      },
    });
  };

  /** @type {import('../resource/FlixPlayerNoSeekDetector').OnDetectedCallback} */
  handleRegularSeekDetected = ({
    regularSeekDuration,
    regularSeekCount,
    messageDuration,
  }) => {
    const { messageId, senderName } = this.props;
    trackMixpanel({
      type: EventTypes.USER_FLIX_REGULAR_SEEK,
      payload: {
        messageId,
        userUsername: senderName,
        regularSeekDuration,
        regularSeekCount,
        messageDuration,
      },
    });
  };

  /** @type {import('../resource/FlixPlayerDisturber').OnDisturbedCallback} */
  handleDisturbed = ({
    action,
    currentTime,
    shuffledPlaybackRate,
    shuffledTime,
  }) => {
    const { messageId, senderName } = this.props;
    trackMixpanel({
      type: EventTypes.USER_FLIX_DISTURBED,
      payload: {
        messageId,
        userUsername: senderName,
        action,
        currentTime,
        shuffledTime,
        shuffledPlaybackRate,
      },
    });
  };

  componentDidMount() {
    if ('onfullscreenchange' in document) {
      document.addEventListener(
        'fullscreenchange',
        this.handleDocumentFullscreenChange
      );
    } else if ('onwebkitfullscreenchange' in document) {
      document.addEventListener(
        'webkitfullscreenchange',
        this.handleDocumentFullscreenChange
      );
    } else {
      this.handleDocumentFullscreenChangeInterval = setInterval(
        this.handleDocumentFullscreenChange,
        1000 // TODO: remote config
      );
    }
    this.seekingObs$ = new Observable(subscriber => {
      this.seekingSubscriber = subscriber;
    });
    this.seekedObs$ = new Observable(subscriber => {
      this.seekedSubscriber = subscriber;
    });

    this.seekingSubscribtion = this.seekingObs$
      .pipe(
        switchMap(startTime => this.seekedObs$.pipe(mapTo(startTime))),
        debounceTime(500)
      )
      .subscribe(startTime => {
        this.isBeginningSeeking = true;
        if (startTime) {
          const { canViewMedia, messageId } = this.props;
          const { currentTime } = this.state;
          const messageStatus = canViewMedia ? 'unlocked' : 'locked';
          if (canViewMedia) {
            trackMixpanel({
              type: EventTypes.USER_FLIX_SEEK_BEHAVIOR,
              payload: {
                viewId: getViewId({
                  pathname: window.location.pathname,
                }),
                messageStatus,
                messageId,
                startTime,
                endTime: currentTime,
              },
            });
          }
        }
      });

    this.applyMarkedDisturbingAction();

    this.getViewScope();
    this.trackedPathname.current = window.location.pathname;
  }

  applyMarkedDisturbingAction = async () => {
    const { shouldDisturb, toggleDisturbing } = this.props;
    const markedAction = await getMarkedDisturbingAction();
    if (markedAction === 'shouldEnable' && !shouldDisturb) {
      toggleDisturbing({
        shouldDisturb: true,
      });
    } else if (markedAction === 'shouldDisable' && shouldDisturb) {
      toggleDisturbing({
        shouldDisturb: false,
      });
    }
  };

  handleEngagementData = ({ isPlaying, isPlayerVisible }) => {
    const {
      updateEngagementData,
      isFree,
      isNewlyUnlocked,
      viewScope,
      mediaType,
      messageId,
      type,
      senderId,
      senderName,
    } = this.props;

    // because FlixPlayer is also used in PostVideoPlayer, let PostVideoPlayer handle engagement data
    if (type === 'post') return;

    if (this.player?.getAssetUri()) {
      updateEngagementData({
        playerType: PlayerType.VOD,
        isFree,
        isNewlyUnlocked,
        viewScope,
        mediaType,
        messageId,
        playerSource: this.player?.getAssetUri(),
        isPlaying,
        isPlayerVisible,
        creatorId: senderId,
        creatorUsername: senderName,
      });
    }
  };

  handleTimeUpdate = (event, player) => {
    // timeupdate, triggered when the playback position changed
    const currentTime = player.currentTime();
    this.setState({ currentTime });
  };

  handleSeeking = () => {
    if (this.isBeginningSeeking) {
      const { currentTime } = this.state;
      this.isBeginningSeeking = false;
      this.startTime = currentTime;
    }
    this.seekingSubscriber?.next(this.startTime);
  };

  handleSeeked = (event, player) => {
    this.seekedSubscriber?.next();
    const { messageId, assetId, senderName, canViewMedia } = this.props;
    const { currentTime, isSeeking, seekingCurrentTime } = this.state;
    if (canViewMedia) {
      // since !canViewMedia will set player loop (trailer.mp4)
      // and cause unnecessary seeked event on replay
      window.dispatchEvent(
        new CustomEvent('globalclick', {
          detail: {
            buttonId: ButtonId.Detail.PlayerControlsSeekbar,
            messageId,
            'user.username': senderName,
            'asset.id': assetId,
            'flix.current.time': currentTime,
            ...createTrackingEvent(event.currentTarget),
            srcTarget: 'component',
          },
        })
      );
    }

    if (!isSeeking && seekingCurrentTime) {
      const currentTimeSynced =
        Math.abs(seekingCurrentTime - currentTime) < 0.01;

      if (currentTimeSynced) {
        this.setState({
          seekingCurrentTime: null,
        });
        if (!this.isPausedBeforeSeeking) {
          player.play().catch(error => {
            this.setState({
              playState: PLAY_STATE.Pause,
              loadState: null,
            });
            if (error.name === 'NotAllowedError') {
              return;
            }
            withScope(scope => {
              scope.setFingerprint(['flix-player', error.name]);
              scope.setExtra('action', 'canplay event');
              scope.setExtra('playState', this.state.playState);
              scope.setExtra('loadState', this.state.loadState);
              captureException(error);
            });
          });
        }
      } else {
        // if missing sync with currentTime and seeking position on seeked event
        // set final seeking position manual to trigger timeupdate again
        player.currentTime(seekingCurrentTime);
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      splashVideo,
      canViewMedia,
      isFullscreen,
      shouldDisturb,
      shouldShowControls,
      messageId,
      setPlayerData,
      trackPlayerEvent,
      timestampOffsetMilliseconds,
    } = this.props;
    const {
      playState,
      isHoveringControls,
      isSeeking,
      isMoving,
      justTapped,
      isMobileControlsToggled,
    } = this.state;
    if (canViewMedia !== prevProps.canViewMedia) {
      this.player?.loop?.(!canViewMedia);
      trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        prevProps,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      this.handleEngagementData({ isPlaying: false });
    }
    if (isFullscreen !== prevProps.isFullscreen) {
      if (getIsOnIOS()) {
        if (isFullscreen) {
          this.attachScrollLock();
        } else {
          this.detachScrollLock();
        }
      }
    }
    if (shouldDisturb !== prevProps.shouldDisturb) {
      if (shouldDisturb) this.mountDisturber(true);
    }

    if (messageId !== prevProps.messageId) {
      this.getViewScope();
      trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        prevProps,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      this.handleEngagementData({ isPlaying: false });
    }

    if (
      playState !== prevState.playState ||
      isHoveringControls !== prevState.isHoveringControls ||
      isSeeking !== prevState.isSeeking ||
      isMoving !== prevState.isMoving ||
      justTapped !== prevState.justTapped ||
      isMobileControlsToggled !== prevState.isMobileControlsToggled
    ) {
      const isPlaying =
        playState === PLAY_STATE.Play || playState === PLAY_STATE.Playing;
      const isPaused = playState === PLAY_STATE.Pause;
      const isEnded = playState === PLAY_STATE.Ended;
      const isSplashSrc = Object.values(splashVideo).includes(
        this.player?.currentSrc()
      );

      const isVideoControlDisplayed =
        shouldShowControls &&
        !isEnded &&
        (isHoveringControls ||
          isSeeking ||
          isMoving ||
          (isPlaying && justTapped && !isSplashSrc) ||
          (isPaused && isMobileControlsToggled));
      setPlayerData({
        isVideoControlDisplayed,
        controlDisplayedMessageId: isVideoControlDisplayed ? messageId : null,
      });
    }
  }

  componentWillUnmount() {
    this.handleEngagementData({ isPlaying: false, isPlayerVisible: false });
    clearTimeout(this.movingTimeout);
    clearTimeout(this.justTappedDelayTimeout);
    clearTimeout(this.justTappedEndTimeout);
    clearTimeout(this.videoControlTimeout);
    this.handleMouseMove.cancel();
    this.detachScrollLock();
    this.seekingSubscribtion?.unsubscribe();
    if ('onfullscreenchange' in document) {
      document.removeEventListener(
        'fullscreenchange',
        this.handleDocumentFullscreenChange
      );
    } else if ('onwebkitfullscreenchange' in document) {
      document.removeEventListener(
        'webkitfullscreenchange',
        this.handleDocumentFullscreenChange
      );
    } else {
      clearInterval(this.handleDocumentFullscreenChangeInterval);
    }
    this.noSeekDetector?.deactivate();
    this.disturber?.deactivate();

    if (this.player) {
      this.player = undefined;
      delete this.player;
    }

    this.props.setPlayerData({ isFullscreen: false });
  }

  render() {
    const {
      notificationId,
      username,
      messageId,
      assetId,
      meId,
      senderName,
      isPreview,
      canViewMedia,
      duration,
      isFullscreen,
      isMuted,
      shouldDisturb,
      checkUserFlixHelpId,
      shouldAutoplayPost,
      shouldShowControls,
      shouldUseSplash,
      shouldShowUnlock,
      isVideoControlDisplayed,
      controlDisplayedMessageId,
      t,
      alt,
      splashVideo,
      isOnPostDetail,
      isOnMobile,
      isFree,
      type,
      option,
      isAesEncrypted,
      hasBlurredUrl,
    } = this.props;
    const {
      playState,
      loadState,
      error,
      isMoving,
      startWatermarkFlag,
      currentTime,
    } = this.state;
    const isError = error !== null;
    const isPaused = playState === PLAY_STATE.Pause;
    const isEnded = playState === PLAY_STATE.Ended;
    const isLoading = loadState === LOAD_STATE.Loading;
    const canWatchMediaWithoutTrailerBlocker = canViewMedia;

    // backend only generate trailer longer than 3mins
    // or AES post without blurredUrl has no preview stream, use trailer instead
    const shouldUseTrailer =
      (isOnPostDetail && isPreview && 180 < duration) ||
      (isOnPostDetail && isAesEncrypted && !hasBlurredUrl && isPreview);
    const showMask =
      isVideoControlDisplayed && controlDisplayedMessageId === messageId;
    const isSplashSrc = Object.values(splashVideo).includes(
      this.player?.currentSrc()
    );
    const isBlobSrc = this.player?.currentSrc?.()?.includes('blob:'); // either splash or trailer
    const showControls =
      shouldShowControls &&
      !isSplashSrc &&
      !isBlobSrc &&
      canWatchMediaWithoutTrailerBlocker;

    const props = {
      onMouseMove: this.isOnMobile ? null : this.handleMouseMove,
      ref: this.containerRef,
    };

    const trackingPayload = {
      'user.username': senderName,
      'asset.id': assetId,
      'flix.current.time': currentTime,
      isMuted,
      messageId,
      assetId,
    };

    const refinedProps = isFullscreen
      ? {
          ...props,
          style: {
            WebkitOverflowScrolling: 'touch',
            cursor: isFullscreen && !isMoving ? 'none' : 'auto',
          },
          onScrollCapture: this.handleChildScrollCapture,
          onWheelCapture: this.handleChildScrollCapture,
          onTouchMoveCapture: this.handleChildScrollCapture,
        }
      : props;

    const nextOption = {
      loop: !canViewMedia,
      muted: canViewMedia ? isMuted : true,
      ...option,
    };
    if (Object.keys(nextOption).length === Object.keys(this.option).length) {
      const isOptionChanged = Object.entries(nextOption).some(
        ([key, value]) => value !== this.option[key]
      );
      if (isOptionChanged) this.option = nextOption;
    } else {
      this.option = nextOption;
    }

    const Component = (
      <StyledFlixPlayer
        {...refinedProps}
        fullscreen={isFullscreen}
        onKeyDown={this.handleKeyDown}
      >
        <FlixUiHandler
          userUsername={senderName}
          messageId={messageId}
          isSuspended={isPreview}
          player={this.player}
        >
          <FlixUiHandlerWrapper>
            <MessageVideoPlayer
              key={this.state.playerKey}
              type={type}
              drmFeatureName={
                type === 'post'
                  ? DrmFeatureName.POST
                  : type === 'flix'
                    ? DrmFeatureName.FLIX
                    : ''
              }
              isAesEncrypted={isAesEncrypted}
              isNeedReloadCycle={true}
              shouldUseTrailer={shouldUseTrailer}
              shouldUseSplash={shouldUseSplash && !isPreview}
              messageId={messageId}
              assetId={assetId}
              // `shouldAutoplayPost` is used for Feed to determinate when to release player instance in ShakaPlayer.
              shouldAutoplayPost={shouldAutoplayPost}
              alt={alt}
              isMuted={canViewMedia ? isMuted : true}
              option={this.option}
              onCanPlay={this.handleCanPlay}
              onEnded={this.handleEnded}
              onError={this.handleError}
              onFullscreenChange={this.handleFullscreenChange}
              onLoadStart={this.handleLoadStart}
              onLoadedData={this.handleLoadedData}
              onLoadedMetadata={this.handleLoadedMetadata}
              onPause={this.handlePause}
              onPlay={this.handlePlay}
              onPlaying={this.handlePlaying}
              onProgress={this.handleProgress}
              onSetup={this.handlePlayerSetup}
              onProgressUpdate={this.handleProgressUpdate}
              onWaiting={this.handleWaiting}
              onVolumeChange={this.handleVolumeChange}
              onSeeked={this.handleSeeked}
              onEnterPictureInPicture={this.handleEnterPictureInPicture}
              onSeeking={this.handleSeeking}
              onTimeupdate={this.handleTimeUpdate}
            >
              {/*
              Since we've reused the player and its container after applying singleton mode,
              all the children under PlayerPortal would be appended to the same player container, which causes problem.
              To deal with that, we have to provide a new container for PlayerPortal to render different children for different post.
              */}
              <PlayerUiContainer ref={this.playerUiContainerRef} />
              <PlayerPortal container={this.playerUiContainerRef?.current}>
                <VideoWatermark
                  isSuspended={isPreview}
                  player={this.player}
                  username={username}
                  userUsername={senderName}
                  messageId={messageId}
                  startWatermarkFlag={startWatermarkFlag}
                />
                {isOnPostDetail &&
                  shouldShowUnlock &&
                  !isFree &&
                  (!canWatchMediaWithoutTrailerBlocker || !meId) && (
                    <PlayerActionButtonWrapper>
                      <FlixPlayerActionButton
                        drmFeatureName={DrmFeatureName.FLIX}
                        messageId={messageId}
                      />
                    </PlayerActionButtonWrapper>
                  )}
                {notificationId && (
                  <StreamingNotificationWrapper
                    isVideoControlDisplayed={isVideoControlDisplayed}
                  >
                    <TopVideoPopupNotification
                      notificationId={notificationId}
                    />
                  </StreamingNotificationWrapper>
                )}
                {/* Controls */}
                {
                  <FlixPlayerGestureDetector
                    containerRef={this.containerRef}
                    isPaused={isPaused}
                    isMuted={isMuted}
                    isEnabled={!!meId}
                    onTogglePlay={this.handlePlayClick}
                    onToggleFullscreen={this.handleFullscreenClick}
                    onToggleMute={this.handleAudioClick}
                    onFastForward={this.fastForward}
                    onJumpToPercentage={this.handleJumpToPercentage}
                    onToggleMobileControls={this.toggleMobileControls}
                    onMobileFastForward={this.handleMobileFastForward}
                    trackingPayload={trackingPayload}
                  >
                    <Mask
                      style={{
                        opacity: showMask ? 1 : 0,
                        pointerEvents: showMask ? 'auto' : 'none',
                      }}
                    >
                      {/* Play button for auto-play blocked browsers */}
                      {!isError && !showControls && isPaused && (
                        <PlayButton
                          data-element_id={
                            isPaused
                              ? ButtonId.Detail.PlayerControlsPlay
                              : ButtonId.Detail.PlayerControlsPause
                          }
                          data-tracking_payload={trackingPayload}
                          aria-label="play the video"
                          onClick={this.handlePlayClick}
                        >
                          <img src={PlayIcon} alt="play back" />
                        </PlayButton>
                      )}
                      {!isError && showControls && (
                        <Controls fullscreen={isFullscreen}>
                          <PlayerControls
                            bufferedTime={this.state.bufferedTime}
                            currentTime={
                              this.state.seekingCurrentTime ||
                              this.state.currentTime
                            }
                            duration={this.state.duration}
                            isFullscreen={isFullscreen}
                            isMuted={isMuted}
                            isPaused={isPaused}
                            isEnded={isEnded}
                            showMute={canViewMedia}
                            showPlay={canViewMedia}
                            showFullscreen={canViewMedia}
                            showSeeker={canViewMedia}
                            onHover={this.handleHover}
                            onAudioClick={this.handleAudioClick}
                            onFullscreenClick={this.handleFullscreenClick}
                            onPlayClick={this.handlePlayClick}
                            onSeekChange={this.handleSeekBarChange}
                            onSeekStart={this.handleSeekBarChangeStart}
                            onSeekEnd={this.handleSeekBarChangeEnd}
                            trackingPayload={trackingPayload}
                          />
                        </Controls>
                      )}
                    </Mask>
                  </FlixPlayerGestureDetector>
                }
                {!isError && isEnded && (
                  <Mask
                    style={{
                      zIndex: flixPlayerZIndex.replayButton,
                      background: 'rgba(25, 25, 25, 0.5)', // No gradient
                    }}
                    data-element_id={ButtonId.Post.ButtonPostVideoReplay}
                    data-tracking_payload={trackingPayload}
                    onClick={e => {
                      if (isOnMobile) this.handleReplayOnClick(e);
                    }}
                  >
                    <ReplayButton
                      data-element_id={ButtonId.Post.ButtonPostVideoReplay}
                      data-tracking_payload={trackingPayload}
                      onClick={this.handleReplayOnClick}
                    >
                      <img src={ReplayIcon} alt="replay" />
                    </ReplayButton>
                  </Mask>
                )}
                {/* Loading indicator */}
                {!isError && isLoading && (
                  <Mask variant="loading" style={{ pointerEvents: 'none' }}>
                    <Loading size={64} type={LoadingType.TEALBLUE} />
                  </Mask>
                )}
                {/* Error */}
                {isError && (
                  <Mask>
                    <ErrorTip>
                      <img src={ErrorIcon} alt="error" />
                      <span>
                        {t('text_load_video_failed', {
                          ns: TranslationNamespace.POST,
                        })}
                      </span>
                    </ErrorTip>
                  </Mask>
                )}
              </PlayerPortal>
              <FlixPlayerZIndexChecker
                player={this.player}
                isSuspended={isPreview}
              />
            </MessageVideoPlayer>
          </FlixUiHandlerWrapper>
        </FlixUiHandler>
        <FlixPlayerHelper
          enabled={shouldDisturb}
          checkUserFlixHelpId={checkUserFlixHelpId}
        />
      </StyledFlixPlayer>
    );

    const portalContainer = getIsInBrowserMainThread()
      ? document.body
      : undefined;

    if (isFullscreen && getIsOnIOS() && !!portalContainer) {
      return createPortal(Component, portalContainer);
    }

    return Component;
  }

  handleMouseMove = throttle(() => {
    this.setState({ isMoving: true });
    clearTimeout(this.movingTimeout);
    this.movingTimeout = setTimeout(() => {
      this.setState({ isMoving: false });
    }, 2000); // TODO: remote config
  }, 1000); // TODO: remote config

  // Seekbar handlers
  handleSeekBarChange = ({ positionPercentage, sliderPosition = 0 }) => {
    if (this.player) {
      const { duration } = this.state;
      let newCurrentTime = null;
      if (Number.isFinite(duration)) {
        newCurrentTime = duration * positionPercentage;
      } else {
        if (positionPercentage === 0) {
          newCurrentTime = 0;
        } else if (positionPercentage === 1) {
          newCurrentTime = Number.MAX_SAFE_INTEGER;
        } else {
          newCurrentTime = sliderPosition;
        }
      }
      this.setState({
        isSeeking: true,
        seekingCurrentTime: newCurrentTime,
      });
      // trigger player timeupdate, seeking and seeked event
      if (this.shouldUpdateCurrentTimeOnSeeking)
        this.player.currentTime(newCurrentTime);
    }
  };
  handleSeekBarChangeStart = () => {
    const { setPlayerData } = this.props;
    setPlayerData({
      isSeeking: true,
    });
    this.setState({
      isSeeking: true,
    });
  };
  handleSeekBarChangeEnd = () => {
    const { setPlayerData } = this.props;
    setPlayerData({
      isSeeking: false,
    });
    this.setState({
      isSeeking: false,
    });
    if (!this.shouldUpdateCurrentTimeOnSeeking) {
      const { seekingCurrentTime } = this.state;
      this.player.currentTime(seekingCurrentTime);
    }
  };

  handleTimecode = () => {
    if (!this.hasTimecodeSeeked && this.player) {
      const { splashVideo, timecode, duration } = this.props;
      const isSplashSrc = Object.values(splashVideo).includes(
        this.player.currentSrc()
      );
      if (!isSplashSrc && timecode && timecode < duration) {
        this.player.currentTime(timecode);
        this.hasTimecodeSeeked = true;
      }
    }
  };

  getViewScope = async () => {
    const { messageId, fetchViewScope, canViewMedia } = this.props;
    if (!canViewMedia) {
      await fetchViewScope({ id: messageId });
    }
  };

  startWatermark = () => this.setState({ startWatermarkFlag: Math.random() });
  // Player handlers
  handleCanPlay = () => {
    this.setState({ loadState: LOAD_STATE.Loaded });
    this.startWatermark();
    this.handleTimecode();
  };
  handleEnded = () => {
    this.setState({ playState: PLAY_STATE.Ended });
  };
  handleError = error => {
    this.setState({
      error: { code: error.code, message: error.message },
    });
  };
  handleLoadStart = () => {
    this.setState({
      error: null,
      loadState: LOAD_STATE.Loading,
    });
  };
  handleLoadedData = () => {
    this.setState({ error: null, loadState: LOAD_STATE.Loaded });
  };
  handleLoadedMetadata = () => {
    this.props.onLoadedMetadata();
    this.setState({
      error: null,
      loadState: LOAD_STATE.Loaded,
    });
  };
  handlePause = () => {
    const { timestampOffsetMilliseconds } = this.props;
    this.props.trackPlayerEvent({
      type: EventTypes.PLAYER_STOPPED,
      timestamp: Date.now() + timestampOffsetMilliseconds,
    });
    this.handleEngagementData({ isPlaying: false });
    this.setState({ playState: PLAY_STATE.Pause });
  };
  handlePlay = () => {
    this.props.setPlayerData({ hasFlixPlayed: true });
    this.setState({ playState: PLAY_STATE.Play });
  };
  handlePlaying = () => {
    const { timestampOffsetMilliseconds } = this.props;
    this.props.trackPlayerEvent({
      type: EventTypes.PLAYER_STARTED,
      timestamp: Date.now() + timestampOffsetMilliseconds,
    });
    this.handleEngagementData({ isPlaying: true, isPlayerVisible: true });
    this.setState({
      playState: PLAY_STATE.Playing,
      loadState: LOAD_STATE.Loaded,
      error: null,
    });
  };
  handleProgress = (event, player) => {
    this.setState({ bufferedTime: player.bufferedEnd() });
  };
  handleWaiting = () => {
    this.setState({ loadState: LOAD_STATE.Loading });
  };
  handleVolumeChange = () => {
    if (!this.player) return;

    // in iOS when a player is loaded with muted attribute
    // and volume is changed with a native mute button
    // we want to make sure muted state is updated
    if (getIsOnIOS()) {
      this.player.muted(this.player.muted());
    }

    const isMuted = this.player.muted();
    if (this.props.isMuted !== isMuted) {
      const isFullscreen = this.player.isFullscreen();
      if (isFullscreen) {
        this.props.setPlayerData({ isMuted });
      } else {
        this.player.muted(this.props.isMuted);
      }
    }
  };

  handleEnterPictureInPicture = () => {
    trackMixpanel({
      type: EventTypes.USER_VIDEO_ENTER_PIP,
      payload: {},
    });
  };

  handlePlayerSetup = player => {
    const {
      shouldDisturb,
      onSetup,
      onFullscreenChange,
      timestampOffsetMilliseconds,
    } = this.props;
    if (this.player) {
      // when splash -> flix
      this.props.trackPlayerEvent({
        type: EventTypes.PLAYER_HIDDEN,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      this.handleEngagementData({ isPlayerVisible: false });
    }
    onSetup?.(player);
    this.player = player;
    this.props.trackPlayerEvent({
      type: EventTypes.PLAYER_VISIBLE,
      timestamp: Date.now() + timestampOffsetMilliseconds,
    });
    this.handleEngagementData({ isPlayerVisible: true });
    this.player.landscapeFullscreen({
      fullscreen: {
        enterOnRotate: !getIsOnIOS(),
        alwaysInLandscapeMode: true,
        iOS: false,
      },
    });
    onFullscreenChange?.(this.player.isFullscreen());

    this.noSeekDetector = FlixPlayerNoSeekDetector.mount(
      player,
      this.handleNoSeekDetected,
      this.handleRegularSeekDetected
    );

    if (shouldDisturb) this.mountDisturber();
  };
  handleProgressUpdate = ({ currentTime, duration }) => {
    const { duration: manifestDuration } = this.props;
    this.setState({
      currentTime,
      // duration will be NaN on iPhone6/S
      duration: duration || manifestDuration,
    });
  };

  // Fullscreen change event handler
  handleFullscreenChange = () => {
    const { onFullscreenChange, setPlayerData } = this.props;
    this.startWatermark();
    const isFullscreen = this.player.isFullscreen();
    setPlayerData({ isFullscreen });
    onFullscreenChange?.(isFullscreen);
  };

  /**
   * Handle document fullscreenchange events
   * Crashes app when user entered fullscreen but element is not player div
   */
  handleDocumentFullscreenChange = () => {
    const playerElement = this.player?.el();
    const fullscreenElement =
      document.fullscreenElement || document.webkitFullscreenElement;
    if (fullscreenElement && fullscreenElement !== playerElement) {
      trackMixpanel({
        type: EventTypes.USER_NON_PLAYER_ENTER_FULLSCREEN,
        payload: {
          nodeHTML: fullscreenElement.outerHTML,
        },
      });
    }
  };

  // Controls handlers
  handleAudioClick = () => {
    const { isMuted } = this.props;
    this.props.setPlayerData({ isMuted: !isMuted });
  };

  handleFullscreenClick = async () => {
    const { isFullscreen, setPlayerData } = this.props;
    if (getIsOnIOS()) {
      await setPlayerData({
        isFullscreen: !isFullscreen,
      });
      this.startWatermark();
    } else {
      if (this.player.isFullscreen()) {
        this.player.exitFullscreen();
      } else {
        this.player.requestFullscreen();
      }
    }
  };

  handlePlayClick = () => {
    const isPaused = this.player.paused();
    if (isPaused) {
      // Play
      this.player.play().catch(error => {
        withScope(scope => {
          scope.setFingerprint(['flix-player', error.name]);
          scope.setExtra('action', 'play button clicked');
          scope.setExtra('playState', this.state.playState);
          scope.setExtra('loadState', this.state.loadState);
          captureException(error);
        });
      });
    } else {
      // Pause
      this.player.pause();
      this.setState({
        isMobileControlsToggled: true,
        justTapped: false,
      });
      clearTimeout(this.justTappedEndTimeout);
    }
  };

  handleReplayOnClick = e => {
    e.stopPropagation();
    // This is for Safari.
    // After adding cues without using Shaka's API
    // (in order to display subtitles properly on Safari),
    // the videos are unable to replay correctly after the end of playing.
    this.player.currentTime(0);

    this.player.play().catch(error => {
      withScope(scope => {
        scope.setFingerprint(['flix-player', error.name]);
        scope.setExtra('action', 'play button clicked');
        scope.setExtra('playState', this.state.playState);
        scope.setExtra('loadState', this.state.loadState);
        captureException(error);
      });
    });
  };

  handleHover = ({ isHovered }) => {
    if (getIsOnMobile()) return;
    this.setState({
      isHoveringControls: isHovered,
    });
  };

  /** @param {number} offset offset in seconds */
  fastForward = offset => {
    if (!this.player) return;
    const { currentTime, duration, playState } = this.state;

    const isEnded = playState === PLAY_STATE.Ended;

    let newCurrentTime;
    if (offset > 0 && isEnded) {
      // If video is ended, play from the beginning
      newCurrentTime = 0;
    } else {
      // clamp target time between the beginning and last 0.1 seconds
      newCurrentTime = Math.max(
        Math.min(currentTime + offset, duration - 0.1),
        0
      );
    }
    this.player.currentTime(newCurrentTime);
    this.setState({ currentTime: newCurrentTime });
  };

  /** @param {number} percentage percentage of duration */
  handleJumpToPercentage = percentage => {
    if (!this.player) return;
    const { duration } = this.state;
    const newCurrentTime = duration * percentage;
    this.player.currentTime(newCurrentTime);
    this.setState({ currentTime: newCurrentTime });
  };

  /** @param {number} offset offset in seconds */
  handleMobileFastForward = offset => {
    this.setState({ justTapped: false });
    clearTimeout(this.justTappedDelayTimeout);
    clearTimeout(this.justTappedEndTimeout);
    this.fastForward(offset);
  };

  toggleMobileControls = () => {
    // Delay actual execution for double tap detecting
    this.justTappedDelayTimeout = setTimeout(() => {
      clearTimeout(this.justTappedEndTimeout);

      const { playState, justTapped, isMobileControlsToggled } = this.state;

      const isPlaying =
        playState === PLAY_STATE.Play || playState === PLAY_STATE.Playing;

      if (isPlaying) {
        this.setState({
          justTapped: !justTapped,
        });
        if (!justTapped) {
          this.justTappedEndTimeout = setTimeout(() => {
            this.setState({ justTapped: false });
          }, JUST_TAPPED_STATE_DURATION);
        }
      } else {
        this.setState({
          isMobileControlsToggled: !isMobileControlsToggled,
        });
      }
    }, MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS);
  };

  // HACK: scroll lock
  attachScrollLock() {
    if (typeof document !== 'undefined') {
      document.addEventListener('wheel', this.handleDocumentScroll, aggressive);
      document.addEventListener(
        'touchmove',
        this.handleDocumentScroll,
        aggressive
      );
      document.addEventListener(
        'touchstart',
        this.handleDocumentTouchStart,
        aggressive
      );
    }
  }

  detachScrollLock() {
    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'wheel',
        this.handleDocumentScroll,
        aggressive
      );
      document.removeEventListener(
        'touchmove',
        this.handleDocumentScroll,
        aggressive
      );
      document.removeEventListener(
        'touchstart',
        this.handleDocumentTouchStart,
        aggressive
      );
    }
  }

  /**@param {TouchEvent} event*/
  handleDocumentTouchStart = e => {
    this.touchStartY = getTouchY(e);
  };

  /**@param {WheelEvent|TouchEvent} e*/
  handleDocumentScroll = e => {
    const delta = e.deltaY || getTouchY(e);
    const sourceEvent = this.shouldPreventQueue.filter(
      event =>
        event.name === e.type &&
        event.delta === delta &&
        event.target === e.target
    )[0];
    // self event, and should be canceled
    if (sourceEvent && sourceEvent.should && event.cancelable) {
      e.preventDefault();
      return;
    }
    // outside
    if (!sourceEvent && event.cancelable) {
      e.preventDefault();
    }
  };

  getDeltaY = e => {
    switch (e.type) {
      case 'wheel':
      case 'scroll':
        return e.deltaY;
      case 'touchmove':
        return this.touchStartY - getTouchY(e);
    }
    throw new Error(`Unexpected event type: ${e.type}`);
  };

  handleChildScrollCapture = e => {
    const deltaY = this.getDeltaY(e);
    const item = {
      name: e.type,
      delta: e.type === 'wheel' ? deltaY : getTouchY(e),
      target: e.target,
      should: getShouldCancelScroll(this.containerRef.current, e, deltaY),
    };
    this.shouldPreventQueue.push(item);
    setTimeout(() => {
      this.shouldPreventQueue = this.shouldPreventQueue.filter(e => e !== item);
    }, 1);
  };
}

FlixPlayer.propTypes = {
  messageId: PropTypes.string.isRequired,
  meId: PropTypes.string,
  senderId: PropTypes.string,
  assetId: PropTypes.string,
  type: PropTypes.string,
  isPreview: PropTypes.bool,
  notificationId: PropTypes.string,
  username: PropTypes.string,
  senderName: PropTypes.string,
  duration: PropTypes.number,
  timestampOffsetMilliseconds: PropTypes.number,
  canViewMedia: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  isMuted: PropTypes.bool,
  shouldAutoplayPost: PropTypes.bool,
  shouldShowControls: PropTypes.bool,
  shouldUseSplash: PropTypes.bool,
  shouldDisturb: PropTypes.bool,
  shouldShowUnlock: PropTypes.bool,
  setPlayerData: PropTypes.func,
  isOnMobile: PropTypes.bool,
  isOnPostDetail: PropTypes.bool,
  isVideoControlDisplayed: PropTypes.bool,
  disableTrackPlayingTime: PropTypes.bool,
  controlDisplayedMessageId: PropTypes.string,
  setMuted: PropTypes.func,
  fetchViewScope: PropTypes.func,
  onFullscreenChange: PropTypes.func,
  onSetup: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  trackPlayerEvent: PropTypes.func,
  t: PropTypes.func.isRequired,
  alt: PropTypes.string,
  timecode: PropTypes.number,
  toggleDisturbing: PropTypes.func.isRequired,
  checkUserFlixHelpId: PropTypes.func,
  splashVideo: PropTypes.object,
  option: PropTypes.object,
  updateEngagementData: PropTypes.func,
  isFree: PropTypes.bool,
  isNewlyUnlocked: PropTypes.bool,
  viewScope: PropTypes.string,
  mediaType: PropTypes.string,
  isAesEncrypted: PropTypes.bool,
  hasBlurredUrl: PropTypes.bool,
};

FlixPlayer.defaultProps = {
  assetId: '',
  meId: '',
  senderId: '',
  type: 'flix',
  isPreview: true,
  senderName: '',
  duration: 0,
  timestampOffsetMilliseconds: 0,
  notificationId: null,
  username: '',
  canViewMedia: null,
  isFullscreen: false,
  isMuted: true,
  shouldAutoplayPost: false,
  shouldShowControls: true,
  shouldUseSplash: false,
  shouldDisturb: false,
  shouldShowUnlock: true,
  setPlayerData: () => null,
  fetchViewScope: () => null,
  isOnMobile: true,
  isOnPostDetail: false,
  isVideoControlDisplayed: false,
  disableTrackPlayingTime: false,
  controlDisplayedMessageId: null,
  setMuted: () => null,
  alt: '',
  timecode: 0,
  splashVideo: {},
  onSetup: () => null,
  onLoadedMetadata: () => null,
  trackPlayerEvent: () => null,
  option: {},
  updateEngagementData: () => null,
  isFree: null,
  isNewlyUnlocked: null,
  viewScope: '',
  mediaType: '',
  isAesEncrypted: false,
  hasBlurredUrl: false,
};

const PlayerUiContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const StyledFlixPlayer = styled.div`
  user-select: none;
  position: relative;
  width: inherit;
  height: inherit;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ fullscreen }) =>
    fullscreen && getIsOnIOS()
      ? css`
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 999;
        `
      : null}
  &&& {
    ${({ fullscreen }) =>
      fullscreen
        ? css`
            video {
              object-fit: contain;
            }
          `
        : null}
  }
`;

const FlixUiHandlerWrapper = styled.div`
  width: inherit;
  height: inherit;
`;

const Controls = styled.div`
  position: absolute;
  right: 0;
  bottom: ${({ fullscreen }) =>
    fullscreen && getIsOnIOS() ? SAFE_AREA_INSET_BOTTOM : 0}px;
  left: 0;
`;

const getVariantBackground = ({ variant }) => {
  switch (variant) {
    case 'loading':
      return 'none';
    case 'mute':
      return 'none';
    default:
      if (getIsOnMobileBySize()) return 'rgba(25, 25, 25, 0.5)';
      else
        return 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.1) 20%, rgba(0, 0, 0, 0.04) 25%, rgba(0, 0, 0, 0.04) 33%, rgba(0, 0, 0, 0.5))';
  }
};

const Mask = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${flixPlayerZIndex.mask};
  background: ${getVariantBackground};
  transition: opacity 0.3s;
  will-change: opacity;
`;

const ErrorTip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #646464;
  font-size: 14px;
  line-height: 1;
  & > img {
    margin-bottom: 12px;
    width: 36px;
    height: 36px;
  }
`;

const PlayButton = styled.button`
  ${ResetButtonStyle};
  & > img {
    display: block;
    width: 80px;
    height: 80px;
  }
`;

const ReplayButton = styled.button`
  ${ResetButtonStyle};
  & > img {
    display: block;
    width: 60px;
    height: 60px;
  }
  cursor: pointer;
`;

const PlayerActionButtonWrapper = styled.div`
  position: absolute;
  z-index: ${flixPlayerZIndex.unlockButton};
  left: 52px;
  right: 52px;
  bottom: 12px;
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  justify-content: center;
  animation: 0.6s forwards ${FadeIn};
`;

const StreamingNotificationWrapper = styled.div.attrs(
  ({ isVideoControlDisplayed }) => ({
    style: {
      zIndex:
        isVideoControlDisplayed && getIsOnMobileBySize()
          ? flixPlayerZIndex.controlDisplayedOnMobileNotification
          : flixPlayerZIndex.notification,
    },
  })
)`
  position: absolute;
  top: 60px;
  right: 0px;
  padding-left: 16px;
  max-width: 100%;
  ${mobileAspectRatio`
    top: 12px;
    @media (orientation: landscape) {
      top: 14px;
    }
  `};
`;

export default withTranslation()(
  withPlayerTrackingEvent(FlixPlayer, VIDEO_CATEGORY.FLIX)
);
